import React, { useEffect, useRef, useState } from 'react';
import * as pdfjs from 'pdfjs-dist';

// Worker PDF.js da unpkg, come in PdfThumb.tsx. Per l'uso offline copiare
// `pdf.worker.min.mjs` in `public/` e puntare a `${process.env.PUBLIC_URL}`.
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface Props {
    blob: Blob | null;
    className?: string;
    /** Zoom in percentuale (100 = adatta alla larghezza del pannello). */
    zoom?: number;
}

/**
 * Viewer dell'anteprima PDF live. Usa PDF.js, che mantiene la posizione di
 * scroll tra un refresh e l'altro; se fallisce o è troppo lento ripiega
 * sull'iframe nativo. Le pagine sono renderizzate in lazy per i documenti lunghi.
 */
const PdfLivePreview: React.FC<Props> = ({ blob, className, zoom = 100 }) => {
    const zoomFactor = (zoom > 0 ? zoom : 100) / 100;
    const containerRef = useRef<HTMLDivElement>(null);
    const tokenRef = useRef(0);
    const topPageRef = useRef(0);
    const pageOffsetRef = useRef(0);
    // Fallback all'iframe nativo se PDF.js non riesce a renderizzare.
    const [useIframe, setUseIframe] = useState(false);
    const [iframeUrl, setIframeUrl] = useState<string | null>(null);

    const onScroll = () => {
        const c = containerRef.current;
        if (!c) return;
        const kids = Array.from(c.querySelectorAll<HTMLElement>('[data-pdf-page]'));
        const st = c.scrollTop;
        let idx = 0;
        for (let i = 0; i < kids.length; i++) {
            if (kids[i].offsetTop <= st + 1) idx = i; else break;
        }
        topPageRef.current = idx;
        pageOffsetRef.current = st - (kids[idx]?.offsetTop ?? 0);
    };

    // URL oggetto per l'eventuale fallback iframe (revocato al cambio blob/unmount).
    useEffect(() => {
        if (!blob) { setIframeUrl(null); return; }
        const url = URL.createObjectURL(blob);
        setIframeUrl(url);
        return () => { try { URL.revokeObjectURL(url); } catch { /* noop */ } };
    }, [blob]);

    useEffect(() => {
        if (!blob) return;
        setUseIframe(false);
        const container = containerRef.current;
        if (!container) return;
        const token = ++tokenRef.current;
        let cancelled = false;
        let pdfDoc: any = null;
        let observer: IntersectionObserver | null = null;
        let rendered = 0;
        // Se entro 9s non è stata renderizzata nessuna pagina, ripiega sull'iframe.
        const watchdog = setTimeout(() => { if (!cancelled && rendered === 0) setUseIframe(true); }, 9000);

        (async () => {
            const data = new Uint8Array(await blob.arrayBuffer());
            if (cancelled || token !== tokenRef.current) return;
            pdfDoc = await pdfjs.getDocument({ data }).promise;
            if (cancelled || token !== tokenRef.current) return;
            if (!pdfDoc.numPages) { setUseIframe(true); return; }

            const width = Math.max(120, (container.clientWidth - 24) * zoomFactor);
            const scales: { h: number; scale: number }[] = [];
            for (let n = 1; n <= pdfDoc.numPages; n++) {
                const page = await pdfDoc.getPage(n);
                if (cancelled || token !== tokenRef.current) return;
                const vp1 = page.getViewport({ scale: 1 });
                const scale = width / vp1.width;
                const vp = page.getViewport({ scale });
                scales.push({ h: vp.height, scale });
                page.cleanup?.();
            }

            container.innerHTML = '';
            const placeholders: HTMLElement[] = [];
            for (let i = 0; i < scales.length; i++) {
                const ph = document.createElement('div');
                ph.dataset.pdfPage = String(i);
                ph.style.cssText =
                    `position:relative;width:${Math.round(width)}px;max-width:none;aspect-ratio:${Math.round(width)} / ${Math.round(scales[i].h)};` +
                    `margin:0 auto 8px;background:#fff;box-shadow:0 1px 4px rgba(0,0,0,0.15);`;
                container.appendChild(ph);
                placeholders.push(ph);
            }

            const restoreIdx = Math.min(Math.max(0, topPageRef.current), placeholders.length - 1);
            if (placeholders[restoreIdx]) {
                container.scrollTop = placeholders[restoreIdx].offsetTop + pageOffsetRef.current;
            }

            const done = new Set<number>();
            observer = new IntersectionObserver((entries) => {
                entries.forEach((entry) => {
                    if (!entry.isIntersecting) return;
                    const i = Number((entry.target as HTMLElement).dataset.pdfPage);
                    if (done.has(i) || cancelled || token !== tokenRef.current) return;
                    done.add(i);
                    (async () => {
                        const page = await pdfDoc.getPage(i + 1);
                        if (cancelled || token !== tokenRef.current) return;
                        const vp = page.getViewport({ scale: scales[i].scale });
                        const canvas = document.createElement('canvas');
                        canvas.width = Math.floor(vp.width);
                        canvas.height = Math.floor(vp.height);
                        canvas.style.cssText = 'width:100%;height:100%;display:block;';
                        const ctx = canvas.getContext('2d');
                        const ph = entry.target as HTMLElement;
                        if (cancelled || token !== tokenRef.current) return;
                        ph.appendChild(canvas);
                        if (ctx) { await page.render({ canvasContext: ctx, viewport: vp }).promise; rendered++; }
                        page.cleanup?.();
                    })().catch(() => { /* pagina non renderizzata: non bloccante */ });
                });
            }, { root: container, rootMargin: '500px 0px' });
            placeholders.forEach(ph => observer!.observe(ph));
        })().catch(() => { if (!cancelled) setUseIframe(true); });

        return () => {
            cancelled = true;
            clearTimeout(watchdog);
            if (observer) observer.disconnect();
            if (pdfDoc) { try { pdfDoc.destroy(); } catch { /* best-effort */ } }
        };
    }, [blob, zoomFactor]);

    return (
        // La classe passata contiene `absolute inset-0`: non aggiungere
        // position:relative inline, annullerebbe l'inset.
        <div className={className}>
            {useIframe && iframeUrl
                ? <iframe key={zoom} src={`${iframeUrl}#zoom=${zoom}`} title="Anteprima PDF" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0, background: '#e2e8f0' }} />
                : <div ref={containerRef} onScroll={onScroll} style={{ position: 'absolute', inset: 0, overflow: 'auto' }} />}
        </div>
    );
};

export default PdfLivePreview;
